import { apiFetchJson } from './apiClient';
import type {
  GradingAnswer,
  GradingCalibrationAnswer,
  GradingCalibrationQuestion,
  GradingDiffFactor,
  GradingFamily,
  GradingQuestion,
  GradingRole,
  GradingRoleCluster,
  GradingSpecialization,
  GradingTreePayload,
} from '../components/Workspace/admin/grading/types';

export async function fetchGradingTree(): Promise<GradingTreePayload> {
  const res = await apiFetchJson<GradingTreePayload>('GET', '/admin/grading/tree');
  if (!res.ok) throw new Error(res.error);
  return res.data;
}

/** Дерево грейдинга для анкеты в сообществе (read-only, любой авторизованный пользователь). */
export async function fetchCommunityGradingTree(): Promise<GradingTreePayload> {
  const res = await apiFetchJson<GradingTreePayload>('GET', '/private-circle/community/grading/tree');
  if (!res.ok) throw new Error(res.error);
  return res.data;
}

let communityTreeCache: GradingTreePayload | null = null;
let communityTreePromise: Promise<GradingTreePayload> | null = null;

export function clearCommunityGradingTreeCache() {
  communityTreeCache = null;
  communityTreePromise = null;
}

export async function fetchCommunityGradingTreeCached(): Promise<GradingTreePayload> {
  if (communityTreeCache) return communityTreeCache;
  if (!communityTreePromise) {
    communityTreePromise = fetchCommunityGradingTree()
      .then((data) => {
        communityTreeCache = data;
        return data;
      })
      .catch((err) => {
        communityTreePromise = null;
        throw err;
      });
  }
  return communityTreePromise;
}

export async function createGradingFamily(name: string): Promise<GradingFamily> {
  const res = await apiFetchJson('POST', '/admin/grading/families', { body: { name } });
  if (!res.ok) throw new Error(res.error);
  return res.data as GradingFamily;
}

export async function updateGradingFamily(id: number, payload: { name?: string; is_visible?: boolean }): Promise<GradingFamily> {
  const res = await apiFetchJson('PATCH', `/admin/grading/families/${id}`, { body: payload });
  if (!res.ok) throw new Error(res.error);
  return res.data as GradingFamily;
}

export async function createGradingRoleCluster(familyId: number, name: string): Promise<GradingRoleCluster> {
  const res = await apiFetchJson('POST', '/admin/grading/role-clusters', { body: { familyId, name } });
  if (!res.ok) throw new Error(res.error);
  return res.data as GradingRoleCluster;
}

export async function updateGradingRoleCluster(id: number, payload: { name?: string; is_visible?: boolean }): Promise<GradingRoleCluster> {
  const res = await apiFetchJson('PATCH', `/admin/grading/role-clusters/${id}`, { body: payload });
  if (!res.ok) throw new Error(res.error);
  return res.data as GradingRoleCluster;
}

export async function createGradingSpecialization(familyId: number, name: string): Promise<GradingSpecialization> {
  const res = await apiFetchJson('POST', '/admin/grading/specializations', { body: { familyId, name } });
  if (!res.ok) throw new Error(res.error);
  return res.data as GradingSpecialization;
}

export async function updateGradingSpecialization(id: number, payload: { name?: string; is_visible?: boolean }): Promise<GradingSpecialization> {
  const res = await apiFetchJson('PATCH', `/admin/grading/specializations/${id}`, { body: payload });
  if (!res.ok) throw new Error(res.error);
  return res.data as GradingSpecialization;
}

export async function createGradingRole(payload: {
  roleClusterId: number;
  name: string;
  gradeLevel: number;
  specializationId?: number | null;
  description?: string;
  minScore?: number | null;
  maxScore?: number | null;
}): Promise<GradingRole> {
  const res = await apiFetchJson('POST', '/admin/grading/roles', { body: payload });
  if (!res.ok) throw new Error(res.error);
  return res.data as GradingRole;
}

export async function updateGradingRole(id: number, payload: Record<string, unknown>): Promise<GradingRole> {
  const res = await apiFetchJson('PATCH', `/admin/grading/roles/${id}`, { body: payload });
  if (!res.ok) throw new Error(res.error);
  return res.data as GradingRole;
}

export async function createGradingDiffFactor(payload: {
  familyId: number;
  name: string;
  description?: string;
  questionHint?: string;
  is_visible?: boolean;
}): Promise<GradingDiffFactor> {
  const res = await apiFetchJson('POST', '/admin/grading/diff-factors', { body: payload });
  if (!res.ok) throw new Error(res.error);
  return res.data as GradingDiffFactor;
}

export async function updateGradingDiffFactor(id: number, payload: Record<string, unknown>): Promise<GradingDiffFactor> {
  const res = await apiFetchJson('PATCH', `/admin/grading/diff-factors/${id}`, { body: payload });
  if (!res.ok) throw new Error(res.error);
  return res.data as GradingDiffFactor;
}

export async function saveGradingQuestion(payload: {
  mode: 'create' | 'edit';
  id?: number;
  diffFactorId: number;
  roleClusterId: number;
  questionText: string;
  name?: string;
  is_visible?: boolean;
  answers: { id?: number; name?: string; answerText: string; matrixDescription?: string; answerScore: number }[];
}): Promise<{ question: GradingQuestion; answers: GradingAnswer[] }> {
  const body = {
    diffFactorId: payload.diffFactorId,
    roleClusterId: payload.roleClusterId,
    questionText: payload.questionText,
    name: payload.name,
    is_visible: payload.is_visible,
    answers: payload.answers,
  };
  const res =
    payload.mode === 'create'
      ? await apiFetchJson('POST', '/admin/grading/questions', { body })
      : await apiFetchJson('PATCH', `/admin/grading/questions/${payload.id}`, { body });
  if (!res.ok) throw new Error(res.error);
  return res.data as { question: GradingQuestion; answers: GradingAnswer[] };
}

export async function saveGradingCalibration(
  familyId: number,
  questionText: string,
  answers: { answerText: string; targetRoleClusterId: number; sortOrder: number }[],
): Promise<{ question: GradingCalibrationQuestion; answers: GradingCalibrationAnswer[] }> {
  const res = await apiFetchJson('PUT', `/admin/grading/families/${familyId}/calibration`, {
    body: { questionText, answers },
  });
  if (!res.ok) throw new Error(res.error);
  return res.data as { question: GradingCalibrationQuestion; answers: GradingCalibrationAnswer[] };
}

async function deleteGrading(path: string): Promise<void> {
  const res = await apiFetchJson('DELETE', path);
  if (!res.ok) throw new Error(res.error);
}

export function deleteGradingFamily(id: number) {
  return deleteGrading(`/admin/grading/families/${id}`);
}

export function deleteGradingRoleCluster(id: number) {
  return deleteGrading(`/admin/grading/role-clusters/${id}`);
}

export function deleteGradingSpecialization(id: number) {
  return deleteGrading(`/admin/grading/specializations/${id}`);
}

export function deleteGradingRole(id: number) {
  return deleteGrading(`/admin/grading/roles/${id}`);
}

export function deleteGradingDiffFactor(id: number) {
  return deleteGrading(`/admin/grading/diff-factors/${id}`);
}

export function deleteGradingQuestion(id: number) {
  return deleteGrading(`/admin/grading/questions/${id}`);
}

export function deleteGradingCalibration(familyId: number) {
  return deleteGrading(`/admin/grading/families/${familyId}/calibration`);
}
